/**
 * EmptyState — single source of truth for "nothing here" screens.
 *
 *   <EmptyState
 *     icon="🛒"
 *     title="Your cart is empty"
 *     description="Add items from our trending stores to get started"
 *     action={{ label: 'Start shopping', onClick: () => nav('/stores') }}
 *   />
 */
import React from 'react';
import { Button } from './Button';
import { css } from './tokens';

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

export interface EmptyStateProps {
  /** Emoji or React node — kept large for emotional weight. */
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  /** Compact density (used inside cards, drawers). */
  compact?: boolean;
  children?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = '✨',
  title,
  description,
  action,
  secondaryAction,
  compact,
  children,
}) => (
  <div
    style={{
      textAlign: 'center',
      padding: compact ? '24px 16px' : '48px 16px',
      color: css.color.text,
    }}
  >
    <div
      style={{
        fontSize: compact ? 40 : 56,
        marginBottom: compact ? 8 : 14,
        lineHeight: 1,
      }}
      aria-hidden
    >
      {icon}
    </div>
    <h3
      style={{
        margin: 0,
        fontFamily: css.font.display,
        fontSize: compact ? 16 : 20,
        fontWeight: 800,
        letterSpacing: '-0.01em',
        color: css.color.text,
      }}
    >
      {title}
    </h3>
    {description && (
      <p
        style={{
          margin: '8px auto 0',
          maxWidth: 360,
          fontSize: 13,
          color: css.color.textMuted,
          lineHeight: 1.5,
        }}
      >
        {description}
      </p>
    )}
    {(action || secondaryAction) && (
      <div
        style={{
          marginTop: compact ? 14 : 22,
          display: 'inline-flex',
          gap: 10,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {action && (
          <Button
            variant="primary"
            size={compact ? 'sm' : 'md'}
            onClick={action.onClick}
            as={action.href ? 'a' : 'button'}
            {...(action.href ? ({ href: action.href } as any) : {})}
          >
            {action.label}
          </Button>
        )}
        {secondaryAction && (
          <Button
            variant="secondary"
            size={compact ? 'sm' : 'md'}
            onClick={secondaryAction.onClick}
            as={secondaryAction.href ? 'a' : 'button'}
            {...(secondaryAction.href ? ({ href: secondaryAction.href } as any) : {})}
          >
            {secondaryAction.label}
          </Button>
        )}
      </div>
    )}
    {children && <div style={{ marginTop: 18 }}>{children}</div>}
  </div>
);

export default EmptyState;

